import { useRef } from 'react';
import { motion } from 'framer-motion';
import profileHero240 from '@/assets/profile_hero-240.jpg';
import profileHero480 from '@/assets/profile_hero-480.jpg';
import profileHero240Webp from '@/assets/profile_hero-240.webp';
import profileHero480Webp from '@/assets/profile_hero-480.webp';
import Card from '@/components/Card';
import CtaButton from '@/components/CtaButton';
import RichText from '@/components/RichText';
import SkillChip from '@/components/SkillChip';
import SEO from '@/components/SEO';
import MetricReadout from '@/components/MetricReadout';
import RecommendationCard from '@/components/RecommendationCard';
import ContactChannels from '@/components/ContactChannels';
import ContactJumpCue from '@/components/ContactJumpCue';
import { useElementInView } from '@/hooks/useElementInView';
import { content } from '@/content/content';

const { hero, sections, about, expertise, skills, experience, recommendations, contact } = content;

interface SectionHeaderProps {
  index: string;
  label: string;
  title: string;
  headingId: string;
}

/** Ruled section header: structural index + mono label + rule + display title. */
const SectionHeader = ({ index, label, title, headingId }: SectionHeaderProps): JSX.Element => (
  <div className="mb-8">
    <div className="flex items-center gap-4">
      <span className="font-mono text-xs font-semibold text-primary-ink dark:text-primary">{index}</span>
      <span className="eyebrow">{label}</span>
      <span className="h-px flex-1 bg-[color:var(--hairline)]" aria-hidden="true" />
    </div>
    <h2
      id={headingId}
      className="mt-4 font-display text-2xl font-bold tracking-tight text-[color:var(--fg)] sm:text-3xl"
    >
      {title}
    </h2>
  </div>
);

const Home = (): JSX.Element => {
  const heroRef = useRef<HTMLElement>(null);
  const contactRef = useRef<HTMLElement>(null);

  // Both seed to `true` so the cue stays hidden until the observers report —
  // otherwise it flashes on screen for the first frame at the top of the page.
  const isHeroInView = useElementInView(heroRef, { initial: true });
  const isContactInView = useElementInView(contactRef, { initial: true });
  const isCueVisible = !isHeroInView && !isContactInView;

  return (
    <>
      <SEO />
      <div className="space-y-24 sm:space-y-28">
        {/* ── Hero ─────────────────────────────────────────────── */}
        <section ref={heroRef} className="pt-4" aria-labelledby="hero-name">
          {/* Spec strip */}
          <div className="mb-10 flex flex-col items-start gap-2.5 border-b border-[color:var(--hairline)] pb-5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4 sm:gap-y-2">
            <span className="eyebrow">{hero.stack}</span>
            <span className="flex items-center gap-2 sm:ml-auto">
              <span className="relative flex h-2 w-2" aria-hidden="true">
                <span className="absolute inline-flex h-full w-full rounded-full bg-primary-dark animate-pulse-signal dark:bg-primary" />
              </span>
              {/* No `whitespace-nowrap`: the tracked mono label overflows 320px viewports.
                  Colour via `--signal-text` (which flips per theme) because `.eyebrow` is
                  defined after the utilities and would otherwise win over `text-primary-ink`. */}
              <span className="eyebrow text-[color:var(--signal-text)]">{hero.liveLabel}</span>
            </span>
          </div>

          <div className="grid items-start gap-10 md:grid-cols-[1fr_auto] md:gap-14">
            {/* Nameplate + statement */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
              className="order-2 md:order-1"
            >
              <h1 id="hero-name" className="flex flex-col gap-3">
                <span className="font-display text-[clamp(2.75rem,9vw,5.25rem)] font-extrabold leading-[0.92] tracking-nameplate text-[color:var(--fg)]">
                  {hero.name}
                </span>
                <span className="font-mono text-[clamp(0.95rem,2.4vw,1.3rem)] font-medium text-primary-ink dark:text-primary">
                  {hero.role}
                </span>
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-[color:var(--fg-muted)]">
                {hero.statement}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <CtaButton to={hero.ctaPrimary.to} variant="solid" ariaLabel={hero.ctaPrimary.ariaLabel}>
                  {hero.ctaPrimary.label}
                </CtaButton>
                <CtaButton to={hero.ctaSecondary.to} variant="ghost" ariaLabel={hero.ctaSecondary.ariaLabel}>
                  {hero.ctaSecondary.label}
                </CtaButton>
              </div>
            </motion.div>

            {/* Technical portrait */}
            <motion.figure
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, ease: 'easeOut', delay: 0.1 }}
              className="relative order-1 mx-auto w-full max-w-[220px] md:order-2 md:mx-0 md:w-[240px] md:max-w-none"
            >
              <div className="relative">
                <picture>
                  <source
                    type="image/webp"
                    srcSet={`${profileHero240Webp} 240w, ${profileHero480Webp} 480w`}
                    sizes="(min-width: 768px) 240px, 220px"
                  />
                  <img
                    src={profileHero480}
                    srcSet={`${profileHero240} 240w, ${profileHero480} 480w`}
                    sizes="(min-width: 768px) 240px, 220px"
                    alt={hero.imageAlt}
                    width="480"
                    height="480"
                    loading="eager"
                    className="aspect-square w-full rounded-sm border border-[color:var(--hairline)] object-cover"
                  />
                </picture>
                {/* Registration ticks */}
                <span className="absolute -left-1 -top-1 h-4 w-4 border-l border-t border-primary-dark dark:border-primary" aria-hidden="true" />
                <span className="absolute -bottom-1 -right-1 h-4 w-4 border-b border-r border-primary-dark dark:border-primary" aria-hidden="true" />
              </div>
              <figcaption className="eyebrow mt-3">Fig.01 — {hero.location}</figcaption>
            </motion.figure>
          </div>

          {/* Telemetry panel */}
          <div className="mt-12">
            <p className="eyebrow mb-3">{hero.metricsLabel}</p>
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-[color:var(--hairline)] bg-[color:var(--hairline)] lg:grid-cols-4">
              {hero.metrics.map((metric, index) => (
                <div key={metric.label} className="bg-[color:var(--panel)]">
                  <MetricReadout metric={metric} index={index} />
                </div>
              ))}
            </div>
            <a
              href={hero.endorsementCue.to}
              aria-label={hero.endorsementCue.ariaLabel}
              className="group mt-2 inline-flex min-h-[44px] items-center gap-1.5 font-mono text-sm font-medium text-[color:var(--fg-muted)] transition-colors hover:text-primary-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-dark dark:hover:text-primary dark:focus-visible:ring-primary"
            >
              {hero.endorsementCue.label}
              <span aria-hidden="true" className="transition-transform group-hover:translate-x-0.5">→</span>
            </a>
          </div>
        </section>

        {/* ── Profile ──────────────────────────────────────────── */}
        <section aria-labelledby="about-heading">
          <SectionHeader index="01" label={sections.about} title={about.heading} headingId="about-heading" />
          <RichText
            segments={about.body}
            className="max-w-3xl text-lg leading-relaxed text-[color:var(--fg-muted)]"
          />
        </section>

        {/* ── Capabilities ─────────────────────────────────────── */}
        <section aria-labelledby="expertise-heading">
          <SectionHeader index="02" label={sections.expertise} title={expertise.heading} headingId="expertise-heading" />
          <RichText segments={expertise.intro} className="mb-8 max-w-3xl text-lg text-[color:var(--fg-muted)]" />
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {expertise.items.map((item, index) => (
              <Card key={item.title} index={index}>
                <h3 className="mb-3 font-display text-xl font-bold text-[color:var(--fg)]">{item.title}</h3>
                <p className="leading-relaxed text-[color:var(--fg-muted)]">{item.description}</p>
              </Card>
            ))}
          </div>
        </section>

        {/* ── Stack ────────────────────────────────────────────── */}
        <section aria-labelledby="skills-heading">
          <SectionHeader index="03" label={sections.skills} title={skills.heading} headingId="skills-heading" />
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {skills.groups.map((skillGroup, index) => (
              <Card key={skillGroup.category} index={index}>
                <h3 className="eyebrow mb-4">{skillGroup.category}</h3>
                <div className="flex flex-wrap gap-2.5">
                  {skillGroup.items.map((skill) => (
                    <SkillChip key={skill} label={skill} />
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* ── Career ledger ────────────────────────────────────── */}
        <section id="career" aria-labelledby="experience-heading" className="scroll-mt-24">
          <SectionHeader index="04" label={sections.experience} title={experience.heading} headingId="experience-heading" />
          <div>
            {experience.roles.map((role, index) => (
              <motion.article
                key={`${role.role}-${role.period}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.45, delay: (index % 3) * 0.08 }}
                className="grid gap-3 border-t border-[color:var(--hairline)] py-7 md:grid-cols-[11rem_1fr] md:gap-8"
              >
                <p className="font-mono text-sm font-medium text-primary-ink dark:text-primary">{role.period}</p>
                <div>
                  <h3 className="font-display text-lg font-bold text-[color:var(--fg)]">{role.role}</h3>
                  <p className="eyebrow mt-1">
                    {role.company}
                    {role.location ? ` · ${role.location}` : ''}
                  </p>
                  <ul className="mt-4 space-y-2.5">
                    {role.achievements.map((achievement) => (
                      <li key={achievement} className="flex gap-3 text-[color:var(--fg-muted)]">
                        <span className="mt-1 shrink-0 font-mono text-primary-ink dark:text-primary" aria-hidden="true">▸</span>
                        <span className="leading-relaxed">{achievement}</span>
                      </li>
                    ))}
                  </ul>

                  {role.engagements && role.engagements.length > 0 && (
                    <div className="mt-5 space-y-5">
                      {role.engagements.map((engagement) => (
                        <div
                          key={engagement.name}
                          className="border-l border-[color:var(--hairline)] pl-4"
                        >
                          <h4 className="font-display text-base font-semibold text-[color:var(--fg)]">
                            {engagement.name}
                          </h4>
                          <p className="font-mono text-xs text-[color:var(--fg-muted)]">{engagement.period}</p>
                          <ul className="mt-3 space-y-2.5">
                            {engagement.achievements.map((achievement) => (
                              <li key={achievement} className="flex gap-3 text-[color:var(--fg-muted)]">
                                <span className="mt-1 shrink-0 font-mono text-primary-ink dark:text-primary" aria-hidden="true">▸</span>
                                <span className="leading-relaxed">{achievement}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}

                  {role.tech && role.tech.length > 0 && (
                    <div className="mt-5 flex flex-wrap gap-2">
                      {role.tech.map((tech) => (
                        <SkillChip key={tech} label={tech} />
                      ))}
                    </div>
                  )}
                </div>
              </motion.article>
            ))}
            <span className="block border-t border-[color:var(--hairline)]" aria-hidden="true" />
          </div>
        </section>

        {/* ── Endorsements ─────────────────────────────────────── */}
        <section id="recommendations" aria-labelledby="recommendations-heading" className="scroll-mt-24">
          <SectionHeader
            index="05"
            label={sections.recommendations}
            title={recommendations.heading}
            headingId="recommendations-heading"
          />
          <RichText
            segments={recommendations.intro}
            className="mb-8 max-w-3xl text-lg text-[color:var(--fg-muted)]"
          />
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {recommendations.items.map((recommendation, index) => (
              <RecommendationCard key={recommendation.name} recommendation={recommendation} index={index} />
            ))}
          </div>
          <a
            href={recommendations.linkedInUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 font-mono text-sm font-medium text-primary-ink transition-opacity duration-150 hover:opacity-70 dark:text-primary"
          >
            View all recommendations on LinkedIn
            <span aria-hidden="true">↗</span>
          </a>
        </section>

        {/* ── Contact ──────────────────────────────────────────── */}
        <section
          id="contact"
          ref={contactRef}
          aria-labelledby="contact-heading"
          className="scroll-mt-24"
        >
          <SectionHeader index="06" label={sections.contact} title={contact.heading} headingId="contact-heading" />
          <RichText
            segments={contact.intro}
            className="mb-8 max-w-3xl text-lg leading-relaxed text-[color:var(--fg-muted)]"
          />
          <p className="eyebrow mb-2">{contact.infoHeading}</p>
          <ContactChannels />
        </section>
      </div>

      <ContactJumpCue
        isVisible={isCueVisible}
        to={contact.jumpCue.to}
        label={contact.jumpCue.label}
        ariaLabel={contact.jumpCue.ariaLabel}
      />
    </>
  );
};

export default Home;
